import { userManagerApi, UserVo, EUserStatus } from '@/axios/user';
import { useRequest, useSetState } from 'ahooks';
import { ColumnsType } from 'antd/es/table';
import { Button, Form, Input, Select, Space, Table, Tag, Typography, message } from 'antd';
import styles from './index.module.less';
import SearchInput from '@/components/SearchInput';
import { useNavigate } from 'react-router-dom';
import { openDeleteModal } from '@/components/Modal/common';
import CommonModalFactory from '@/plugins/ModalFactory';
import { useEffect } from 'react';
import AccessButton from '@/components/AccessButton';
import OperatorBtns, { BaseButtonConfig } from '@/components/Table/operateBtns';
import CommonTable from '@/components/Table';
const { Text } = Typography;
const userStatusMap: Record<EUserStatus, { label: string; color: string }> = {
  [EUserStatus.Normal]: { label: '正常', color: '#36B274' },
  [EUserStatus.Locked]: { label: '锁定', color: '#F2883D' },
  [EUserStatus.Deleted]: { label: '删除', color: '#E34D59' },
};
const UserListPage = () => {
  const [params, setParams] = useSetState({
    pageIndex: 1,
    pageSize: 10,
    search: '',
    status: undefined as EUserStatus | undefined,
  });
  const { data, loading } = useRequest(
    () => {
      return userManagerApi.list(params);
    },
    {
      refreshDeps: [params],
    }
  );
  const columns: ColumnsType<UserVo> = [
    {
      title: '名称',
      dataIndex: 'nickname',
      fixed: 'left',
      width: 200,
      ellipsis: true,
      render: (value: any) => {
        return (
          <Text
            ellipsis={{
              tooltip: value,
            }}
          >
            {value}
          </Text>
        );
      },
    },
    {
      title: '角色',
      dataIndex: 'roles',
      width: 200,
      ellipsis: true,
      render: (roles, record) => {
        return (
          <Text
            ellipsis={{
              tooltip: roles?.join(','),
            }}
          >
            {roles?.join(',')}
          </Text>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      filters: Object.entries(userStatusMap).map(([value, config]) => ({
        text: config.label,
        value,
      })),
      filterMultiple: false,
      filteredValue: params.status ? [params.status] : null,
      render: (status: EUserStatus) => {
        const config = userStatusMap[status];
        return config ? <Tag color={config.color}>{config.label}</Tag> : '-';
      },
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 200,
      ellipsis: true,
      render: (val) => new Date(val).toLocaleString(),
    },
    {
      title: '操作',
      fixed: 'right',
      width: 120,
      render: (_, record) => {
        const btns: BaseButtonConfig[] = [
          {
            type: 'edit',
            onClick: () => {
              openEditUserModal(
                {
                  userId: record.userId,
                },
                { title: '编辑用户' },
                () => setParams({ ...params })
              );
            },
            permission: ['sys_user:edit'], // 需要编辑权限
          },
          {
            type: 'common',
            text: record.status === EUserStatus.Locked ? '解锁' : '锁定',
            onClick: async () => {
              await userManagerApi[record.status === EUserStatus.Locked ? 'unlockUser' : 'lockUser']([record.userId!]);
              message.success(`用户${record.status === EUserStatus.Locked ? '解锁' : '锁定'}成功`);
              setParams({ ...params });
            },
            permission: ['sys_user:edit'], // 需要删除权限
          },
        ];
        return <OperatorBtns btns={btns} />;
      },
    },
  ];
  return (
    <div className={styles.user_list_page}>
      <div className={styles.toolbar}>
        <AccessButton permission={['sys_user:create']}>
          <Button
            type="primary"
            onClick={() => {
              openEditUserModal(undefined, { title: '创建用户' }, () => {
                setParams({ pageIndex: 1 });
              });
            }}
          >
            新增用户
          </Button>
        </AccessButton>
        <SearchInput
          onSearch={(val) => {
            setParams({ pageIndex: 1, search: val });
          }}
          style={{ width: '200px', marginLeft: 'auto' }}
          placeholder="请输入用户名称"
        />
      </div>
      <div className={styles.tableContainer}>
        <CommonTable<UserVo>
          loading={loading}
          rowKey={'userId'}
          columns={columns}
          dataSource={data?.records || []}
          total={data?.total}
          params={params}
          setParams={setParams}
          onChange={(_, filters, __, extra) => {
            if (extra.action === 'filter') {
              setParams({
                pageIndex: 1,
                status: filters.status?.[0] as EUserStatus | undefined,
              });
            }
          }}
        />
      </div>
    </div>
  );
};

export default UserListPage;
const openEditUserModal = CommonModalFactory<{
  userInfo?: UserVo;
}>(
  {
    width: 600,
    title: '编辑用户',
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    const isEdit = !!data?.userInfo;
    const { data: roleList = [], loading: roleListLoading } = useRequest(() => userManagerApi.roles());

    useEffect(() => {
      if (data?.userInfo) {
        // 编辑模式：填充现有数据
        form.setFieldsValue({
          nickname: data.userInfo.nickname,
          email: data.userInfo.email,
          roleIds: data.userInfo.roles?.map((role) => role.roleId),
          status: data.userInfo.status,
          password: data.userInfo.password,
        });
      }
    }, [data?.userInfo, form]);

    useEffect(() => {
      setOkFn(async () => {
        try {
          const values = await form.validateFields();

          if (isEdit && data?.userInfo?.userId) {
            // 编辑用户
            const updateData: any = {
              userId: data.userInfo.userId,
              nickname: values.nickname,
              email: values.email,
              roleIds: values.roleIds,
              status: values.status,
            };

            // 如果填写了密码，则包含密码字段
            if (values.password) {
              updateData.password = values.password;
            }

            await userManagerApi.update(updateData);
            message.success('用户更新成功');
          } else {
            // 创建用户
            await userManagerApi.create({
              nickname: values.nickname,
              email: values.email,
              password: values.password,
              roleIds: values.roleIds,
            });
            message.success('用户创建成功');
          }

          return true;
        } catch (error: any) {
          console.error('表单验证失败:', error);
          if (error?.errorFields) {
            message.error('请检查表单填写');
          }
          return false;
        }
      });
    }, [form, isEdit, data?.userInfo, setOkFn]);

    return (
      <Form form={form} layout="vertical" autoComplete="off">
        <Form.Item
          label="用户昵称"
          name="nickname"
          rules={[
            { required: true, message: '请输入用户昵称' },
            { max: 50, message: '昵称不能超过50个字符' },
          ]}
        >
          <Input placeholder="请输入用户昵称" />
        </Form.Item>

        <Form.Item
          label="邮箱"
          name="email"
          rules={[
            { required: true, message: '请输入邮箱' },
            { type: 'email', message: '请输入有效的邮箱地址' },
          ]}
        >
          <Input placeholder="请输入邮箱" disabled={isEdit} />
        </Form.Item>

        <Form.Item
          label="密码"
          name="password"
          rules={[
            { required: !isEdit, message: '请输入密码' },
            { min: 6, message: '密码至少6个字符' },
          ]}
        >
          <Input.Password placeholder="请输入密码" />
        </Form.Item>

        <Form.Item label="角色" name="roleIds" rules={[{ required: true, message: '请选择角色' }]}>
          <Select
            mode="multiple"
            placeholder="请选择角色"
            loading={roleListLoading}
            options={roleList.map((role) => ({
              value: role.roleId as string | number,
              label: role.roleName || role.roleCode || String(role.roleId || ''),
            }))}
          />
        </Form.Item>

        {isEdit && (
          <Form.Item label="状态" name="status" rules={[{ required: true, message: '请选择状态' }]}>
            <Select
              placeholder="请选择状态"
              options={[
                { value: EUserStatus.Normal, label: '正常' },
                { value: EUserStatus.Locked, label: '锁定' },
                { value: EUserStatus.Deleted, label: '删除' },
              ]}
            />
          </Form.Item>
        )}
      </Form>
    );
  },
  async (props) => {
    if (props?.userId) {
      // 编辑用户
      const userInfo = await userManagerApi.get(props.userId);
      return { userInfo };
    } else {
      // 创建用户
      return {};
    }
  }
);
